/**
 * Paragraphize dense CMS SOM / regulatory prose for readable UI (AB#10692).
 * Prefers blank-line structure when present; otherwise breaks on known section
 * headers, bullets, NOTE labels, and sentence boundaries for long blocks.
 */

/** Major CMS SOM appendix headings (longest first). */
export const SOM_SECTION_HEADERS = [
  "KEY ELEMENTS OF NON-COMPLIANCE",
  "DEFICIENCY CATEGORIZATION",
  "INVESTIGATIVE PROCEDURES",
  "INVESTIGATIVE PROCEDURE",
  "DEFINITIONS",
  "GUIDANCE",
  "INTENT",
] as const;

const HEADER_ALT = SOM_SECTION_HEADERS.map((h) =>
  h.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"),
).join("|");

/** Split before ALL-CAPS section headers (with or without surrounding newlines). */
const HEADER_SPLIT_RE = new RegExp(
  `(?=(?:^|[\\n\\r]|\\s)(?:${HEADER_ALT})(?=\\s|$|[:.]))`,
  "i",
);

const MAX_SENTENCE_CHUNK = 420;

function collapseHorizontal(text: string): string {
  return text.replace(/[^\S\n]+/g, " ").replace(/ *\n */g, "\n").trim();
}

function splitOnBulletsAndNotes(block: string): string[] {
  let t = block.trim();
  if (!t) return [];

  // Normalize bullets onto their own lines
  t = t.replace(/\s*•\s*/g, "\n• ");
  // NOTE: label
  t = t.replace(/\s*(NOTE:)\s*/gi, "\n$1 ");

  return t
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean);
}

function splitOnHeaders(block: string): string[] {
  const trimmed = block.trim();
  if (!trimmed) return [];

  // If headers appear as standalone lines, split there first
  const withBreaks = trimmed.replace(
    new RegExp(`(?:^|\\n)\\s*(${HEADER_ALT})\\s*(?=\\n|$)`, "gi"),
    "\n\n$1\n",
  );

  // Also split when header is jammed mid-line: "…text. INVESTIGATIVE PROCEDURES Review…"
  const mid = withBreaks.replace(
    new RegExp(`([a-z0-9.|;])\\s+(${HEADER_ALT})(?=\\s)`, "gi"),
    "$1\n\n$2",
  );

  const parts = mid
    .split(/\n{2,}/)
    .map((p) => p.trim())
    .filter(Boolean);

  if (parts.length <= 1 && HEADER_SPLIT_RE.test(trimmed)) {
    return trimmed
      .split(HEADER_SPLIT_RE)
      .map((p) => p.trim())
      .filter(Boolean);
  }
  return parts.length > 0 ? parts : [trimmed];
}

function splitLongOnSentences(block: string): string[] {
  if (block.length <= MAX_SENTENCE_CHUNK) return [block];

  // Keep bullet / NOTE lines intact
  if (/^•/.test(block) || /^NOTE:/i.test(block)) return [block];
  // Keep short header-only lines
  const upper = block.toUpperCase();
  if (
    SOM_SECTION_HEADERS.some((h) => upper === h || upper.startsWith(`${h} `)) &&
    block.length < 120
  ) {
    return [block];
  }

  // Avoid lookbehind for broader browser targets: split after .!? + space
  const sentences: string[] = [];
  const re = /[.!?]\s+(?=[A-Z0-9§("])/g;
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(block)) !== null) {
    sentences.push(block.slice(last, m.index + 1).trim());
    last = m.index + m[0].length;
  }
  if (last < block.length) sentences.push(block.slice(last).trim());
  if (sentences.length <= 1) return [block];

  const chunks: string[] = [];
  let buf = "";
  for (const s of sentences) {
    if (!s) continue;
    const next = buf ? `${buf} ${s}` : s;
    if (next.length > MAX_SENTENCE_CHUNK && buf) {
      chunks.push(buf.trim());
      buf = s;
    } else {
      buf = next;
    }
  }
  if (buf.trim()) chunks.push(buf.trim());
  return chunks.length > 0 ? chunks : [block];
}

function furtherSplitDenseBlock(block: string): string[] {
  const byHeader = splitOnHeaders(block);
  const out: string[] = [];
  for (const part of byHeader) {
    const byBullet = splitOnBulletsAndNotes(part);
    for (const piece of byBullet) {
      // Attach bare header lines to following body when possible
      out.push(...splitLongOnSentences(piece));
    }
  }
  return out.filter(Boolean);
}

/**
 * Split CMS SOM / citation prose into readable paragraph blocks.
 */
export function paragraphizeSomText(text: string): string[] {
  if (!text?.trim()) return [];

  const normalized = collapseHorizontal(text.replace(/\r\n/g, "\n"));
  if (!normalized) return [];

  const blankSplit = normalized
    .split(/\n{2,}/)
    .map((b) => b.trim())
    .filter(Boolean);

  // When blank lines already structure the text, refine each block lightly
  if (blankSplit.length > 1) {
    return blankSplit.flatMap((block) => {
      // Single-newline blocks that are already short: keep
      if (block.length <= MAX_SENTENCE_CHUNK && !/[•]/.test(block)) {
        return [block.replace(/\n+/g, " ").trim()].filter(Boolean);
      }
      return furtherSplitDenseBlock(block.replace(/\n+/g, " ").trim());
    });
  }

  // One blob (possibly with single newlines) — treat as dense
  const dense = normalized.replace(/\n+/g, " ").replace(/\s+/g, " ").trim();
  return furtherSplitDenseBlock(dense);
}

/**
 * First readable narrative paragraph for Summary cards.
 * Uses paragraphizeSomText so dense single-line API blobs do not fill the Summary.
 */
export function firstSomSummaryParagraph(preamble: string): string {
  const blocks = paragraphizeSomText(preamble);
  if (blocks.length === 0) return "";

  // Prefer first non-header, non-bullet block when available
  const narrative = blocks.find(
    (b) =>
      !SOM_SECTION_HEADERS.some((h) => b.toUpperCase().startsWith(h)) &&
      !b.startsWith("•") &&
      !/^NOTE:/i.test(b),
  );
  return (narrative ?? blocks[0]).trim();
}
